'use client'

import { apiFetch } from '@/lib/api-client'
import { useState, useEffect } from 'react'
import { useRouter, useSearchParams } from '@/lib/react-compat/navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Sprout, AlertCircle, Eye, EyeOff, ArrowLeft, Loader2 } from 'lucide-react'
import { useAuth } from '@/components/auth-context'
import { AuthPageSkeleton } from '@/components/loading-skeletons'
import Link from '@/lib/react-compat/link'
import { useI18n } from '@/components/i18n-provider'

function RegisterPageInner() {
  const { t } = useI18n()
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: '',
    phone: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [registrationAllowed, setRegistrationAllowed] = useState(true)
  const [checkingStatus, setCheckingStatus] = useState(true)
  const { isAuthenticated, login } = useAuth()
  const router = useRouter()
  const searchParams = useSearchParams()

  // 检查注册功能是否开启
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const res = await apiFetch('/api/auth/registration-status')
        const data = await res.json()
        setRegistrationAllowed(data.allowed ?? true)
      } catch {
        // 请求失败时默认允许注册
        setRegistrationAllowed(true)
      } finally {
        setCheckingStatus(false)
      }
    }
    checkRegistrationStatus()
  }, [])

  // 如果已经登录，重定向到首页
  useEffect(() => {
    if (isAuthenticated) {
      const returnUrl = searchParams.get('returnUrl') || '/'
      router.replace(returnUrl)
    }
  }, [isAuthenticated, router, searchParams])

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }))
    setError('')
    setSuccess('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    // 前端验证
    if (formData.password.length < 6) {
      setError(t('register.errorPasswordShort'))
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.errorPasswordMismatch'))
      return
    }

    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError(t('register.errorEmailInvalid'))
      return
    }

    setIsLoading(true)

    try {
      const res = await apiFetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        }),
      })

      const data = await res.json()

      if (res.ok) {
        setSuccess(t('register.success'))
        // 自动登录
        const loginSuccess = await login(formData.username, formData.password)
        if (loginSuccess) {
          // 登录成功，跳转到首页
          setTimeout(() => {
            const returnUrl = searchParams.get('returnUrl') || '/'
            router.replace(returnUrl)
          }, 1000)
        } else {
          // 登录失败，跳转到登录页
          setTimeout(() => {
            router.push('/login')
          }, 2000)
        }
      } else {
        setError(data.error || t('register.errorFailed'))
      }
    } catch {
      setError(t('register.errorFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const backButton = (
    <div className="absolute left-4 top-4">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => router.replace('/')}
        className="text-muted-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        返回
      </Button>
    </div>
  )

  const brandBlock = (
    <div className="mb-6 flex flex-col items-center gap-2 text-center">
      <div className="flex size-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Sprout className="size-5" />
      </div>
      <h1 className="text-xl font-semibold">{t('brand')}</h1>
      <p className="text-sm text-muted-foreground">{t('register.subtitle')}</p>
    </div>
  )

  // 如果已经登录，显示加载状态
  if (isAuthenticated) {
    return <AuthPageSkeleton />
  }

  // 注册功能已关闭
  if (!registrationAllowed) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center bg-muted p-6 md:p-10">
        {backButton}
        <div className="w-full max-w-sm">
          {brandBlock}
          <Card>
            <CardContent className="pt-6">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{t('register.registrationDisabled')}</AlertDescription>
              </Alert>
              <div className="mt-6 text-center">
                <Link
                  href="/login"
                  className="text-sm font-medium text-foreground underline-offset-4 hover:underline"
                >
                  {t('register.backToLogin')}
                </Link>
              </div>
            </CardContent>
          </Card>
          <p className="mt-6 text-center text-xs text-muted-foreground">{t('login.slogan')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-muted p-6 md:p-10">
      {backButton}
      <div className="w-full max-w-sm">
        {brandBlock}

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-lg">{t('register.submit')}</CardTitle>
            <CardDescription>{t('register.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">{t('register.usernameLabel')}</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder={t('register.usernamePlaceholder')}
                  value={formData.username}
                  onChange={handleChange('username')}
                  autoComplete="username"
                  required
                  disabled={isLoading}
                  minLength={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">{t('register.nameLabel')}</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t('register.namePlaceholder')}
                  value={formData.name}
                  onChange={handleChange('name')}
                  autoComplete="name"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('register.emailLabel')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('register.emailPlaceholder')}
                    value={formData.email}
                    onChange={handleChange('email')}
                    autoComplete="email"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('register.phoneLabel')}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder={t('register.phonePlaceholder')}
                    value={formData.phone}
                    onChange={handleChange('phone')}
                    autoComplete="tel"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('register.passwordLabel')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('register.passwordPlaceholder')}
                    value={formData.password}
                    onChange={handleChange('password')}
                    className="pr-10"
                    autoComplete="new-password"
                    required
                    disabled={isLoading}
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={isLoading}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('register.confirmPasswordLabel')}</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={t('register.confirmPasswordPlaceholder')}
                    value={formData.confirmPassword}
                    onChange={handleChange('confirmPassword')}
                    className="pr-10"
                    autoComplete="new-password"
                    required
                    disabled={isLoading}
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={isLoading}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert>
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={isLoading || checkingStatus}>
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {isLoading ? t('register.submitting') : t('register.submit')}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                {t('register.hasAccount')}{' '}
                <Link href="/login" className="font-medium text-foreground underline-offset-4 hover:underline">
                  {t('register.loginLink')}
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">{t('login.slogan')}</p>
      </div>
    </div>
  )
}

export default function RegisterPage() {
  return <RegisterPageInner />
}
